import { flow, identity, pipe } from "fp-ts/function";
import { either } from "fp-ts";
import { Either } from "fp-ts/Either";
import * as t from "io-ts";
import turfBbox from "@turf/bbox";
import turfLength from "@turf/length";
import { BBox2d, FeatureCollection } from "@turf/helpers/dist/js/lib/geojson";
import { Distance, distanceCodec } from "../../domain/common";
import { errorsToMessage } from "../fp";
// @ts-ignore
import { gpx } from "@tmcw/togeojson";

// const geometryCodec = t.type({
//     type: t.union([
//         t.literal("Point"),
//         t.literal("MultiPoint"),
//         t.literal("LineString"),
//         t.literal("MultiLineString"),
//         t.literal("Polygon"),
//         t.literal("MultiPolygon"),
//         t.literal("GeometryCollection"),
//     ]),
// });
//
// const featureCodec = t.type({ type: t.literal("Feature") });
// const featureCollectionCodec = t.type({ type: t.literal("FeatureCollection") });
//
// const geoJsonCodec = t.union(
//     [geometryCodec, featureCodec, featureCollectionCodec],
//     "GeoJson"
// );

export const geoJsonCodec = t.type({}, "GeoJson");
// todo schema not complete, just used parts
export type GeoJson = t.TypeOf<typeof geoJsonCodec>;

const geoJsonParse = flow(
    geoJsonCodec.decode,
    either.mapLeft((e) => `json parse failed: ${e}`)
);

export const fromGpx = (gpxStr: string): Either<string, GeoJson> =>
    pipe(
        either.tryCatch(() => {
            return new DOMParser().parseFromString(gpxStr, "application/xml");
        }, String),
        either.chain((doc) =>
            either.tryCatch(
                () => gpx(doc),
                (e) => `gpx parse failed: ${e}`
            )
        ),
        either.chain(geoJsonParse)
    );

type Position = Array<number>; // [lon, lat, ele?]
type Geometry =
    | { type: "LineString"; coordinates: Array<Position> }
    | { type: "MultiLineString"; coordinates: Array<Array<Position>> }
    | { type: string };
type Feature = { type: "Feature"; geometry: Geometry | null };

const geometryLines = (geometry: Geometry | null): Array<Array<Position>> => {
    if (geometry == null) return [];
    if (geometry.type === "LineString")
        return [(geometry as { coordinates: Array<Position> }).coordinates];
    if (geometry.type === "MultiLineString")
        return (geometry as { coordinates: Array<Array<Position>> })
            .coordinates;
    return []; // waypoints etc. are not exported
};

const geoJsonLines = (geoJson: GeoJson): Array<Array<Position>> => {
    const g = geoJson as
        | { type: "FeatureCollection"; features: Array<Feature> }
        | Feature
        | Geometry;
    if (g.type === "FeatureCollection")
        return (g as { features: Array<Feature> }).features.flatMap((f) =>
            geometryLines(f.geometry)
        );
    if (g.type === "Feature") return geometryLines((g as Feature).geometry);
    return geometryLines(g as Geometry);
};

const escapeXml = (s: string) =>
    s.replace(
        /[<>&'"]/g,
        (c) =>
            ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;" }[c] ??
            "&quot;")
    );

const gpxTrkpt = ([lon, lat, ele]: Position) =>
    `<trkpt lat="${lat}" lon="${lon}">${
        ele != null ? `<ele>${ele}</ele>` : ""
    }</trkpt>`;

/**
 * Serializes track geometry as a single GPX track, one trkseg per line,
 * in the given order (a race course may pass the same segment repeatedly).
 */
export const toGpx = (geoJsonList: Array<GeoJson>, name: string): string =>
    [
        `<?xml version="1.0" encoding="UTF-8"?>`,
        `<gpx version="1.1" creator="xathlo" xmlns="http://www.topografix.com/GPX/1/1">`,
        `<trk><name>${escapeXml(name)}</name>`,
        ...geoJsonList
            .flatMap(geoJsonLines)
            .map((line) => `<trkseg>${line.map(gpxTrkpt).join("")}</trkseg>`),
        `</trk>`,
        `</gpx>`,
    ].join("\n");

export const boundingBox = (geoJson: GeoJson): Either<unknown, BBox2d> =>
    pipe(
        either.tryCatch(() => turfBbox(geoJson), identity),
        either.map(([a, b, c, d]) => [a, b, c, d])
    );

export const distance = (geoJson: GeoJson): Either<unknown, Distance> =>
    pipe(
        either.tryCatch(
            () => turfLength(geoJson as FeatureCollection, { units: "meters" }),
            String
        ),
        either.chain(
            flow(
                Math.round,
                distanceCodec.decode,
                either.mapLeft(errorsToMessage)
            )
        )
    );
